import { useEffect, useMemo, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Plus, Search, Trash2, Check, X } from 'lucide-react';
import { Container, PageHeader } from '@vnetwork/ui/layout';
import {
  Button,
  Card,
  CardBody,
  CardHeader,
  CardTitle,
  CardDescription,
  IconButton,
  Input,
  Spinner,
} from '@vnetwork/ui/primitives';
import {
  DataTable,
  EmptyState,
  Stat,
  StatGroup,
  StatusPill,
  type StatusTone,
  type ColumnDef,
} from '@vnetwork/ui/data';
import { Alert, ConfirmDialog } from '@vnetwork/ui/feedback';
import {
  useBatch,
  useBatchCandidates,
  useAddBatchItem,
  useRemoveBatchItem,
  useUpdateBatchItem,
  type BatchItem,
  type Candidate,
  type BatchStatusValue,
} from '@/api/reconciliation-batches';
import { formatVND } from '@/api/reconciliation';
import { TabError, TabLoading } from '@/pages/customers/detail/tab-states';
import { EntityLink } from '@/components/entity-link';
import { TableLink } from '@/components/table-link';

/* ── Status pill ─────────────────────────────────────────────────────────── */

const BATCH_STATUS_LABEL: Record<BatchStatusValue, string> = {
  draft: 'Nháp',
  ready: 'Sẵn sàng',
  running: 'Đang chạy',
  done: 'Hoàn tất',
  cancelled: 'Đã huỷ',
};
const BATCH_STATUS_TONE: Record<BatchStatusValue, StatusTone> = {
  draft: 'neutral',
  ready: 'info',
  running: 'warning',
  done: 'success',
  cancelled: 'muted',
};

const errMsg = (e: unknown): string => {
  const ax = e as { response?: { data?: { message?: string; error?: string } }; message?: string };
  return ax?.response?.data?.message || ax?.response?.data?.error || ax?.message || 'Thao tác thất bại.';
};

/* ── Candidate search (debounced) ────────────────────────────────────────── */

function CandidateSearch({
  existingIds,
  onAdd,
  adding,
}: {
  existingIds: Set<number>;
  onAdd: (c: Candidate) => void;
  adding: number | null;
}) {
  const [term, setTerm] = useState('');
  const [debounced, setDebounced] = useState('');

  useEffect(() => {
    const h = setTimeout(() => setDebounced(term.trim()), 300);
    return () => clearTimeout(h);
  }, [term]);

  const enabled = debounced.length >= 2;
  const { data, isFetching, isError } = useBatchCandidates(
    { q: debounced, page: 1, page_size: 10 },
    { enabled },
  );
  const results = useMemo(() => data?.results ?? [], [data]);

  return (
    <Card>
      <CardHeader>
        <CardTitle>Tìm dịch vụ</CardTitle>
        <CardDescription>Nhập tên/mã dịch vụ hoặc khách hàng để thêm vào danh sách.</CardDescription>
      </CardHeader>
      <CardBody>
        <Input
          value={term}
          onChange={(e) => setTerm(e.target.value)}
          placeholder="Tìm dịch vụ, khách hàng…"
          startSlot={<Search size={15} />}
          style={{ maxWidth: 320 }}
        />
        <div style={{ marginTop: 'var(--vn-space-3)' }}>
          {!enabled ? (
            <span style={{ fontSize: 'var(--vn-font-size-sm)', color: 'var(--vn-fg-muted)' }}>
              Nhập ít nhất 2 ký tự để tìm.
            </span>
          ) : isError ? (
            <Alert tone="danger" title="Không tải được kết quả tìm kiếm" />
          ) : isFetching ? (
            <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--vn-space-2)', color: 'var(--vn-fg-muted)' }}>
              <Spinner size="sm" /> Đang tìm…
            </div>
          ) : results.length === 0 ? (
            <span style={{ fontSize: 'var(--vn-font-size-sm)', color: 'var(--vn-fg-muted)' }}>
              Không tìm thấy dịch vụ nào.
            </span>
          ) : (
            <div style={{ display: 'grid', gap: 'var(--vn-space-2)' }}>
              {results.map((c) => {
                const already = existingIds.has(c.service_id);
                return (
                  <div
                    key={c.service_id}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      gap: 'var(--vn-space-3)',
                      padding: 'var(--vn-space-2) var(--vn-space-3)',
                      border: '1px solid var(--vn-border-subtle)',
                      borderRadius: 'var(--vn-radius-md)',
                      background: 'var(--vn-bg-surface)',
                    }}
                  >
                    <div style={{ minWidth: 0 }}>
                      <div style={{ fontSize: 'var(--vn-font-size-sm)', color: 'var(--vn-fg-default)' }}>
                        #{c.service_id} — {c.service_name || '—'}
                      </div>
                      <div style={{ fontSize: 'var(--vn-font-size-xs)', color: 'var(--vn-fg-muted)' }}>
                        {c.company_name || c.customer_name || '—'}
                        {c.product_group_name ? ` · ${c.product_group_name}` : ''}
                      </div>
                    </div>
                    <Button
                      size="sm"
                      variant={already ? 'ghost' : 'secondary'}
                      startIcon={already ? <Check size={14} /> : <Plus size={14} />}
                      disabled={already || adding === c.service_id}
                      onClick={() => onAdd(c)}
                    >
                      {already ? 'Đã thêm' : adding === c.service_id ? <Spinner size="sm" /> : 'Thêm'}
                    </Button>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </CardBody>
    </Card>
  );
}

/* ── Items table columns ─────────────────────────────────────────────────── */

function buildItemColumns(
  onToggleIncluded: (item: BatchItem) => void,
  onEditNote: (item: BatchItem) => void,
  onRemove: (item: BatchItem) => void,
  busyServiceId: number | null,
): ColumnDef<BatchItem, unknown>[] {
  return [
    {
      id: 'included',
      header: '',
      cell: ({ row }) => {
        const it = row.original;
        const included = it.included ?? true;
        return (
          <IconButton
            variant="ghost"
            size="sm"
            aria-label={included ? 'Bỏ khỏi lượt chạy' : 'Đưa vào lượt chạy'}
            disabled={busyServiceId === it.service_id}
            onClick={() => onToggleIncluded(it)}
          >
            {included ? (
              <Check size={15} style={{ color: 'var(--vn-success-text)' }} />
            ) : (
              <X size={15} style={{ color: 'var(--vn-fg-muted)' }} />
            )}
          </IconButton>
        );
      },
    },
    {
      id: 'service',
      header: 'Dịch vụ',
      cell: ({ row }) => (
        <EntityLink
          to={`/services/${row.original.service_id}`}
          id={row.original.service_id}
          name={row.original.service_name || `#${row.original.service_id}`}
        />
      ),
    },
    {
      id: 'customer',
      header: 'Khách hàng',
      cell: ({ row }) =>
        row.original.customer_id ? (
          <EntityLink
            to={`/customers/${row.original.customer_id}`}
            id={row.original.customer_id}
            name={row.original.company_name || row.original.customer_name || '—'}
          />
        ) : (
          <span style={{ color: 'var(--vn-fg-muted)' }}>—</span>
        ),
    },
    {
      id: 'invoice',
      header: 'Hoá đơn',
      cell: ({ row }) =>
        row.original.invoice_code ? (
          <TableLink to={`/invoices/${row.original.invoice_id}`}>{row.original.invoice_code}</TableLink>
        ) : (
          <span style={{ color: 'var(--vn-fg-muted)' }}>—</span>
        ),
    },
    {
      id: 'total',
      header: 'Thành tiền',
      cell: ({ row }) => (
        <span style={{ fontVariantNumeric: 'tabular-nums', color: 'var(--vn-fg-default)' }}>
          {typeof row.original.invoice_total === 'number' ? formatVND(row.original.invoice_total) : '—'}
        </span>
      ),
    },
    {
      id: 'note',
      header: 'Ghi chú',
      cell: ({ row }) => (
        <button
          type="button"
          onClick={() => onEditNote(row.original)}
          style={{
            background: 'none',
            border: 'none',
            padding: 0,
            cursor: 'pointer',
            textAlign: 'left',
            color: row.original.note ? 'var(--vn-fg-default)' : 'var(--vn-fg-muted)',
            fontSize: 'var(--vn-font-size-sm)',
            fontFamily: 'var(--vn-font-sans)',
          }}
        >
          {row.original.note || 'Thêm ghi chú…'}
        </button>
      ),
    },
    {
      id: 'actions',
      header: '',
      cell: ({ row }) => (
        <IconButton
          variant="ghost"
          size="sm"
          aria-label="Xoá khỏi danh sách"
          disabled={busyServiceId === row.original.service_id}
          onClick={() => onRemove(row.original)}
        >
          <Trash2 size={15} style={{ color: 'var(--vn-danger-text)' }} />
        </IconButton>
      ),
    },
  ];
}

/* ── Page ────────────────────────────────────────────────────────────────── */

/**
 * /reconciliation/batches/:id — build/curate one reconciliation batch.
 * Search candidates (debounced) → add; DataTable of items with per-row
 * include-toggle, inline note edit (ConfirmDialog with an Input), and remove.
 * Every mutation is USER-triggered; removes go behind a ConfirmDialog.
 */
export default function ReconciliationBatchBuilderPage() {
  const { id = '' } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const batchQuery = useBatch(id);
  const addItem = useAddBatchItem(id);
  const removeItem = useRemoveBatchItem(id);
  const updateItem = useUpdateBatchItem(id);

  const [actionError, setActionError] = useState<string | null>(null);
  const [adding, setAdding] = useState<number | null>(null);
  const [busyServiceId, setBusyServiceId] = useState<number | null>(null);
  const [removeTarget, setRemoveTarget] = useState<BatchItem | null>(null);
  const [noteTarget, setNoteTarget] = useState<BatchItem | null>(null);
  const [noteDraft, setNoteDraft] = useState('');

  const batch = batchQuery.data;
  const items = useMemo(() => batch?.items ?? [], [batch]);
  const existingIds = useMemo(() => new Set(items.map((i) => i.service_id)), [items]);

  if (batchQuery.isLoading) {
    return (
      <Container size="full">
        <TabLoading rows={6} />
      </Container>
    );
  }
  if (batchQuery.isError || !batch) {
    return (
      <Container size="full">
        <TabError title="Không tải được danh sách đối soát" onRetry={() => batchQuery.refetch()} />
      </Container>
    );
  }

  const includedCount = items.filter((i) => (i.included ?? true)).length;
  const totalAmount = items.reduce((sum, i) => sum + (i.invoice_total ?? 0), 0);
  const status = batch.status;

  const handleAdd = async (c: Candidate) => {
    setActionError(null);
    setAdding(c.service_id);
    try {
      await addItem.mutateAsync({
        service_id: c.service_id,
        invoice_id: c.invoice_id ?? undefined,
        customer_id: c.customer_id ?? undefined,
      });
    } catch (e) {
      setActionError(errMsg(e));
    } finally {
      setAdding(null);
    }
  };

  const handleToggleIncluded = async (item: BatchItem) => {
    setActionError(null);
    setBusyServiceId(item.service_id);
    try {
      await updateItem.mutateAsync({
        serviceId: item.service_id,
        body: { included: !(item.included ?? true) },
      });
    } catch (e) {
      setActionError(errMsg(e));
    } finally {
      setBusyServiceId(null);
    }
  };

  const openNote = (item: BatchItem) => {
    setNoteTarget(item);
    setNoteDraft(item.note ?? '');
  };

  const handleSaveNote = async () => {
    if (!noteTarget) return;
    setActionError(null);
    setBusyServiceId(noteTarget.service_id);
    try {
      await updateItem.mutateAsync({ serviceId: noteTarget.service_id, body: { note: noteDraft } });
      setNoteTarget(null);
    } catch (e) {
      setActionError(errMsg(e));
    } finally {
      setBusyServiceId(null);
    }
  };

  const handleRemove = async () => {
    if (!removeTarget) return;
    setActionError(null);
    setBusyServiceId(removeTarget.service_id);
    try {
      await removeItem.mutateAsync(removeTarget.service_id);
      setRemoveTarget(null);
    } catch (e) {
      setActionError(errMsg(e));
    } finally {
      setBusyServiceId(null);
    }
  };

  const itemColumns = buildItemColumns(
    handleToggleIncluded,
    openNote,
    (it) => setRemoveTarget(it),
    busyServiceId,
  );

  const period =
    batch.period_start || batch.period_end
      ? `${batch.period_start ?? '…'} – ${batch.period_end ?? '…'}`
      : 'Chưa đặt kỳ';

  return (
    <Container size="full">
      <div style={{ display: 'grid', gap: 'var(--vn-space-4)' }}>
        <PageHeader
          title={batch.name}
          description={`Danh sách đối soát · ${period}`}
          actions={
            <Button variant="secondary" onClick={() => navigate('/reconciliation')}>
              Về danh sách kỳ
            </Button>
          }
        />

        {actionError && (
          <Alert
            tone="danger"
            title="Thao tác thất bại"
            actions={
              <Button size="sm" variant="ghost" onClick={() => setActionError(null)}>
                Bỏ qua
              </Button>
            }
          >
            {actionError}
          </Alert>
        )}

        <Card flat>
          <CardBody>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 'var(--vn-space-4)' }}>
              <StatGroup columns={3}>
                <Stat label="Tổng mục" value={items.length.toLocaleString('vi-VN')} />
                <Stat label="Sẽ chạy" value={includedCount.toLocaleString('vi-VN')} />
                <Stat label="Tổng tiền" value={formatVND(totalAmount)} />
              </StatGroup>
              <StatusPill tone={BATCH_STATUS_TONE[status] ?? 'neutral'}>
                {BATCH_STATUS_LABEL[status] ?? status}
              </StatusPill>
            </div>
          </CardBody>
        </Card>

        <CandidateSearch existingIds={existingIds} onAdd={handleAdd} adding={adding} />

        <Card>
          <CardHeader>
            <CardTitle>Mục trong danh sách ({items.length})</CardTitle>
          </CardHeader>
          <CardBody>
            {items.length === 0 ? (
              <EmptyState
                icon={<Plus size={24} />}
                title="Chưa có mục nào"
                description="Dùng ô tìm kiếm phía trên để thêm dịch vụ vào danh sách."
              />
            ) : (
              <DataTable
                columns={itemColumns}
                data={items}
                getRowId={(r) => String(r.service_id)}
              />
            )}
          </CardBody>
        </Card>
      </div>

      {/* ── Remove item confirm ── */}
      <ConfirmDialog
        open={removeTarget !== null}
        onOpenChange={(o) => !o && setRemoveTarget(null)}
        title="Xoá mục khỏi danh sách"
        description={
          removeTarget
            ? `Xoá dịch vụ "${removeTarget.service_name || `#${removeTarget.service_id}`}" khỏi danh sách đối soát?`
            : ''
        }
        destructive
        confirmLabel="Xoá"
        loading={busyServiceId === removeTarget?.service_id}
        onConfirm={handleRemove}
      />

      {/* ── Edit note ── */}
      <ConfirmDialog
        open={noteTarget !== null}
        onOpenChange={(o) => !o && setNoteTarget(null)}
        title="Ghi chú"
        description={noteTarget ? `Ghi chú cho "${noteTarget.service_name || `#${noteTarget.service_id}`}"` : ''}
        confirmLabel="Lưu"
        loading={busyServiceId === noteTarget?.service_id}
        onConfirm={handleSaveNote}
      >
        <Input
          value={noteDraft}
          onChange={(e) => setNoteDraft(e.target.value)}
          placeholder="Nhập ghi chú…"
          autoFocus
        />
      </ConfirmDialog>
    </Container>
  );
}
